import React, { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import './WhatsAppFloat.css';

const DEFAULT_PHONE = '34626927704';
const DEFAULT_MESSAGE = 'Hola, me gustaria recibir mas informacion.';

// IT-716: el botón arranca minimizado y solo se despliega si el usuario lo pide
// explícitamente; la sesión recuerda ese "mostrado", no el ocultado.
const STORAGE_KEY = 'whatsapp_float_shown';

const getSectionLabel = (pathname) => {
  const cleanPath = pathname && pathname !== '/' ? pathname : '/inicio';
  const trimmed = cleanPath.replace(/^\/+/, '');
  if (!trimmed) return 'inicio';

  return decodeURIComponent(trimmed).replace(/[-_]/g, ' ');
};

const buildWhatsAppUrl = (pathname) => {
  const rawPhone = process.env.REACT_APP_WHATSAPP_PHONE || DEFAULT_PHONE;
  const phone = rawPhone.replace(/\D/g, '');
  const baseMessage = process.env.REACT_APP_WHATSAPP_MESSAGE || DEFAULT_MESSAGE;
  const sectionLabel = getSectionLabel(pathname);
  const fullMessage = `${baseMessage} Seccion: ${sectionLabel}.`;
  const encodedMessage = fullMessage ? `?text=${encodeURIComponent(fullMessage)}` : '';

  return `https://wa.me/${phone}${encodedMessage}`;
};

const WhatsAppFloat = () => {
  const location = useLocation();
  const whatsappUrl = buildWhatsAppUrl(location.pathname);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem(STORAGE_KEY) !== '1');

  const handleHide = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setHidden(true);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const handleShow = useCallback(() => {
    setHidden(false);
    sessionStorage.setItem(STORAGE_KEY, '1');
  }, []);

  if (hidden) {
    return (
      <button
        className="whatsapp-float whatsapp-float--minimized"
        onClick={handleShow}
        aria-label="Mostrar botón de WhatsApp"
        title="Mostrar WhatsApp"
      >
        <svg viewBox="0 0 32 32" width="16" height="16" aria-hidden="true" focusable="false">
          <path
            d="M19.11 17.87c-.26-.13-1.55-.76-1.79-.85-.24-.09-.41-.13-.58.13-.17.26-.67.85-.82 1.03-.15.17-.3.2-.56.07-.26-.13-1.1-.4-2.1-1.29-.78-.69-1.31-1.54-1.46-1.8-.15-.26-.02-.4.11-.53.12-.12.26-.3.39-.45.13-.15.17-.26.26-.43.09-.17.04-.33-.02-.46-.06-.13-.58-1.39-.79-1.9-.21-.5-.43-.43-.58-.44h-.5c-.17 0-.43.06-.65.3-.22.24-.85.83-.85 2.03 0 1.2.87 2.36 1 2.52.13.17 1.7 2.59 4.12 3.63 2.42 1.04 2.42.69 2.86.65.44-.04 1.55-.63 1.76-1.24.22-.61.22-1.13.15-1.24-.06-.11-.24-.17-.5-.3zM16.03 4.64c-6.28 0-11.39 5.11-11.39 11.39 0 2.01.53 3.98 1.53 5.72L4.5 27.36l5.77-1.52a11.35 11.35 0 0 0 5.76 1.56h.01c6.28 0 11.39-5.11 11.39-11.39 0-3.04-1.19-5.9-3.34-8.05a11.35 11.35 0 0 0-8.06-3.32zm0 20.87h-.01a9.49 9.49 0 0 1-4.85-1.34l-.35-.2-3.42.9.91-3.33-.22-.34a9.46 9.46 0 0 1-1.46-5.07c0-5.23 4.26-9.49 9.5-9.49 2.53 0 4.91.99 6.69 2.77a9.39 9.39 0 0 1 2.79 6.7c0 5.24-4.26 9.5-9.49 9.5z"
            fill="currentColor"
          />
        </svg>
      </button>
    );
  }

  return (
    <div className="whatsapp-float-wrapper">
      <button
        className="whatsapp-float__close"
        onClick={handleHide}
        aria-label="Ocultar botón de WhatsApp"
        title="Ocultar"
      >
        ×
      </button>
      <a
        className="whatsapp-float"
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Abrir chat de WhatsApp"
        title="WhatsApp"
      >
        <svg
          className="whatsapp-float__icon"
          viewBox="0 0 32 32"
          width="28"
          height="28"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M19.11 17.87c-.26-.13-1.55-.76-1.79-.85-.24-.09-.41-.13-.58.13-.17.26-.67.85-.82 1.03-.15.17-.3.2-.56.07-.26-.13-1.1-.4-2.1-1.29-.78-.69-1.31-1.54-1.46-1.8-.15-.26-.02-.4.11-.53.12-.12.26-.3.39-.45.13-.15.17-.26.26-.43.09-.17.04-.33-.02-.46-.06-.13-.58-1.39-.79-1.9-.21-.5-.43-.43-.58-.44h-.5c-.17 0-.43.06-.65.3-.22.24-.85.83-.85 2.03 0 1.2.87 2.36 1 2.52.13.17 1.7 2.59 4.12 3.63 2.42 1.04 2.42.69 2.86.65.44-.04 1.55-.63 1.76-1.24.22-.61.22-1.13.15-1.24-.06-.11-.24-.17-.5-.3zM16.03 4.64c-6.28 0-11.39 5.11-11.39 11.39 0 2.01.53 3.98 1.53 5.72L4.5 27.36l5.77-1.52a11.35 11.35 0 0 0 5.76 1.56h.01c6.28 0 11.39-5.11 11.39-11.39 0-3.04-1.19-5.9-3.34-8.05a11.35 11.35 0 0 0-8.06-3.32zm0 20.87h-.01a9.49 9.49 0 0 1-4.85-1.34l-.35-.2-3.42.9.91-3.33-.22-.34a9.46 9.46 0 0 1-1.46-5.07c0-5.23 4.26-9.49 9.5-9.49 2.53 0 4.91.99 6.69 2.77a9.39 9.39 0 0 1 2.79 6.7c0 5.24-4.26 9.5-9.49 9.5z"
            fill="currentColor"
          />
        </svg>
      </a>
    </div>
  );
};

export default WhatsAppFloat;
